import React from 'react';
import { Link } from 'react-router-dom';
import { Car, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';

/**
 * CustomerPortalLayout - Standalone layout for the AI Garage customer portal
 * Used for: /garage, /login, /signup, /profile
 * Features: Clean design, Kakoullas branding, no website nav/footer
 */
const CustomerPortalLayout = ({ children }) => {
  const { isAuthenticated, logout, user } = useAuth();

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Portal Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/garage" className="flex items-center gap-3 group">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center group-hover:bg-blue-700 transition-colors">
                <Car className="h-6 w-6 text-white" />
              </div>
              <div>
                <span className="text-lg font-bold text-gray-900">AI Garage</span>
                <span className="block text-xs text-gray-500">Customer Portal</span>
              </div>
            </Link>

            {/* Right Side */}
            <div className="flex items-center gap-3">
              {isAuthenticated ? (
                <>
                  <Link to="/garage" className="text-sm text-gray-600 hover:text-blue-600 transition-colors hidden sm:block">
                    My Vehicles
                  </Link>
                  <Link to="/profile" className="text-sm text-gray-600 hover:text-blue-600 transition-colors hidden sm:block">
                    Profile
                  </Link>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-semibold">
                      {user?.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <span className="text-sm text-gray-700 font-medium hidden sm:block">
                      {user?.name?.split(' ')[0] || 'User'}
                    </span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={handleLogout}
                    className="text-gray-500 hover:text-red-600 hover:bg-red-50"
                    data-testid="portal-logout-btn"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" size="sm" className="text-gray-700 hover:text-blue-600">
                      Login
                    </Button>
                  </Link>
                  <Link to="/signup">
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                      Get Started
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Simple Footer */}
      <footer className="bg-white border-t border-gray-200 py-6 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Car className="h-5 w-5 text-blue-600" />
              <span className="text-sm text-gray-600">
                © {new Date().getFullYear()} Kakoullas Service Center
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <Link to="/" className="hover:text-blue-600 transition-colors">
                Main Website
              </Link>
              <span>•</span>
              <Link to="/contact" className="hover:text-blue-600 transition-colors">
                Contact Us
              </Link>
              <span>•</span>
              <a href="tel:+35799491454" className="hover:text-blue-600 transition-colors">
                +357 99 491 454
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CustomerPortalLayout;
